import type { CignaManager, CignaPrincipal } from '@uit/shared/schemas/rcf';
import { useState } from 'react';
import s from './rcf-settings.module.scss';

// Cigna managers (T-113) — name + email + the Cigna principal they roll up to.
// At RCF time (T-115) the manager AND their principal are both email recipients.
// Props-driven (shares principals with the principals section via
// RcfReferenceSections); onChange reloads both so a principal add/remove is
// reflected here immediately (dropdown + the SET-NULL of orphaned managers).
interface Props {
  managers: CignaManager[] | undefined;
  principals: CignaPrincipal[];
  onChange: () => void;
}

export function CignaManagersSection({ managers, principals, onChange }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [principalId, setPrincipalId] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function add(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      const r = await fetch('/api/settings/cigna-managers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          principal_id: principalId === '' ? null : principalId,
        }),
      });
      if (!r.ok) {
        setActionError(r.status === 403 ? 'You do not have permission.' : `Could not add (${r.status}).`);
        return;
      }
      setName('');
      setEmail('');
      setPrincipalId('');
    } finally {
      setBusy(false);
      onChange();
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      const r = await fetch(`/api/settings/cigna-managers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!r.ok && r.status !== 204) setActionError(`Could not remove (${r.status}).`);
    } finally {
      setBusy(false);
      onChange();
    }
  }

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>Cigna managers</h2>
      <p className={s.sectionSub}>
        The Cigna managers a resource can report to, and which Cigna principal each rolls up to.
        Selected per resource on the RCF (T-115); the manager and their principal are both added to
        the RCF email recipients.
      </p>

      {actionError && (
        <div className={s.error} role="alert">
          {actionError}
        </div>
      )}
      {!managers && <div className={s.muted}>Loading…</div>}

      {managers && managers.length > 0 && (
        <table className={s.table}>
          <thead>
            <tr>
              <th scope="col">Manager</th>
              <th scope="col">Email</th>
              <th scope="col">Principal</th>
              <th scope="col" className={s.right}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {managers.map((m) => (
              <tr key={m.id}>
                <td>{m.name}</td>
                <td>{m.email}</td>
                <td>
                  {m.principal ? (
                    <>
                      <span className={s.pill}>{m.principal.name}</span>
                      <div className={s.subEmail}>{m.principal.email}</div>
                    </>
                  ) : (
                    <span className={`${s.pill} ${s.pillNone}`}>no principal</span>
                  )}
                </td>
                <td className={s.right}>
                  <button
                    type="button"
                    className={s.removeBtn}
                    disabled={busy}
                    onClick={() => void remove(m.id)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {managers && managers.length === 0 && (
        <div className={s.muted}>No managers yet — add one below.</div>
      )}

      <div className={s.addRow}>
        <div className={s.field}>
          <label className={s.label} htmlFor="cm-name">
            Manager name
          </label>
          <input
            id="cm-name"
            className={s.input}
            value={name}
            placeholder="Jordan Reyes"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="cm-email">
            Manager email
          </label>
          <input
            id="cm-email"
            type="email"
            className={s.input}
            value={email}
            placeholder="jordan.reyes@cigna.com"
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className={s.field}>
          <label className={s.label} htmlFor="cm-principal">
            Principal
          </label>
          <select
            id="cm-principal"
            className={s.select}
            value={principalId}
            onChange={(e) => setPrincipalId(e.target.value)}
          >
            <option value="">— None —</option>
            {principals.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className={s.addBtn}
          disabled={busy || name.trim() === '' || email.trim() === ''}
          onClick={() => void add()}
        >
          Add manager
        </button>
      </div>
      {principals.length === 0 && (
        <div className={s.hint}>Add a Cigna principal first to assign managers to one.</div>
      )}
    </section>
  );
}
